import {
    Hydrate,
    QueryClient,
    QueryClientProvider,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import React from 'react'
import 'react-loading-skeleton/dist/skeleton.css'

import { InscalarSDK } from '@ilg-core/inscalar-web'
import {
    GlobalFooter,
    GlobalNavigation,
    getStateVariant,
} from '@mediaingenuity/totallymoney.globalassets'
import { captureException, setUser } from '@sentry/nextjs'
import { useSetAtom } from 'jotai'
import type { AppProps } from 'next/app'
import { ThemeProvider } from 'styled-components'

import totallyMoneyTheme from '@ilg-totallymoney/ui/theme/TotallyMoneyTheme'

import { acquisitionTrackingAtom } from '@atoms/acquisitionTracking'

import LogEvents from '@components/LogEvents'

import { BasinProvider } from '@context/BasinContext'
import { FeatureFlagProvider } from '@context/FeatureFlagContext'
import { InscalarProvider } from '@context/InscalarProvider'

import GlobalStyle from '../styles/GlobalStyles'

export default function MyApp({ Component, pageProps, router }: AppProps) {
    const setAcquisitionTracking = useSetAtom(acquisitionTrackingAtom)

    const [queryClient] = React.useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    mutations: {
                        onError: error => {
                            captureException(error)
                        },
                    },
                    queries: {
                        onError: error => {
                            captureException(error)
                        },
                    },
                },
            })
    )

    const variant = getStateVariant()
    const hideNavigation = router.query.hidenavigation
    const environment =
        process.env.NEXT_PUBLIC_APP_ENV === 'development' ||
        process.env.NEXT_PUBLIC_APP_ENV == 'test'
            ? 'staging'
            : process.env.NEXT_PUBLIC_APP_ENV

    React.useEffect(() => {
        if (router.query.customerId != null) {
            const customerId = router.query.customerId as string

            setUser({
                username: customerId,
                id: customerId,
            })
        }
    }, [router.query.customerId])

    React.useEffect(() => {
        const acquisitionTracking =
            window.sessionStorage.getItem('registrationTracking') ||
            window.sessionStorage.getItem('trackingData')

        if (acquisitionTracking) {
            const parsedJson = JSON.parse(acquisitionTracking)

            if (
                !parsedJson ||
                !('basinPayload' in parsedJson) ||
                !('dataLayerPayload' in parsedJson)
            ) {
                throw Error('Invalid tracking data')
            }

            try {
                setAcquisitionTracking(parsedJson)
                window.sessionStorage.removeItem('registrationTracking')
                window.sessionStorage.removeItem('trackingData')
            } catch (error) {
                console.warn('Error decoding registration tracking')
            }
        }
    }, [])

    return (
        <QueryClientProvider client={queryClient}>
            <Hydrate state={pageProps.dehydratedState}>
                <InscalarProvider>
                    <BasinProvider environment={environment}>
                        <FeatureFlagProvider>
                            <ThemeProvider theme={totallyMoneyTheme}>
                                <GlobalStyle />
                                <LogEvents />
                                <main>
                                    {!hideNavigation ? (
                                        <GlobalNavigation
                                            variant={variant}
                                            environment={environment}
                                            onBeforeLogout={() => {
                                                return InscalarSDK.logout('1.1')
                                            }}
                                        />
                                    ) : null}
                                    <Component {...pageProps} />
                                    <GlobalFooter
                                        environment={environment}
                                        defaultVariant={variant}
                                    />
                                </main>
                            </ThemeProvider>
                        </FeatureFlagProvider>
                    </BasinProvider>
                </InscalarProvider>
            </Hydrate>
            <ReactQueryDevtools />
        </QueryClientProvider>
    )
}
